import { useMemo, useState } from "react";
import { CategoryDialog, ProductDialog, SupplierDialog } from "./CatalogDialogs";
import CatalogSupplierRow from "./catalog/CatalogSupplierRow";
import CategoriesTab from "./catalog/CategoriesTab";
import ProductsTab from "./catalog/ProductsTab";
import CatalogToolbar from "./catalog/CatalogToolbar";
import CatalogConfirmDialog from "./catalog/CatalogConfirmDialog";
import { useCatalogMutations } from "../hooks/useCatalogMutations";
import { isMain, visibleProducts } from "../lib/permissions";

export default function CatalogPanel({
  user,
  products,
  suppliers,
  categories,
  onProductsChange,
  onSuppliersChange,
  onCategoriesChange,
}) {
  const [tab, setTab] = useState("products");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [productDialog, setProductDialog] = useState({ open: false, product: null });
  const [supplierDialog, setSupplierDialog] = useState({ open: false, supplier: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null });
  const [confirm, setConfirm] = useState(null);

  const main = isMain(user);

  const scoped = useMemo(
    () => (main ? products : visibleProducts(user, products)),
    [user, products, main]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter((p) => {
      const catOk = cat === "All" || p.category === cat;
      const qOk = !q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
      return catOk && qOk;
    });
  }, [scoped, search, cat]);

  const supplierById = useMemo(() => {
    const m = new Map(suppliers.map((s) => [s.id, s]));
    return (id) => m.get(id);
  }, [suppliers]);

  const productCountBySupplier = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.supplierId] = (m[p.supplierId] || 0) + 1; });
    return m;
  }, [products]);

  const productCountByCategory = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      if (!p.category) return;
      m[p.category] = (m[p.category] || 0) + 1;
    });
    return m;
  }, [products]);

  const categoryChips = useMemo(() => ["All", ...categories.map((c) => c.name)], [categories]);

  const canEditProduct = (p) =>
    main || (p.assignedRestaurants || []).includes(user.restaurant) || p.ownerRestaurant === user.restaurant;

  const {
    saveProduct, deleteProduct,
    saveSupplier, deleteSupplier,
    saveCategory, deleteCategory,
  } = useCatalogMutations({
    categories,
    productCountBySupplier,
    productCountByCategory,
    onProductsChange,
    onSuppliersChange,
    onCategoriesChange,
  });

  const onConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === "product") deleteProduct(confirm.id);
    else if (confirm.kind === "supplier") deleteSupplier(confirm.id);
    else if (confirm.kind === "category") deleteCategory(confirm.id);
    setConfirm(null);
  };

  return (
    <section>
      <CatalogToolbar
        tab={tab}
        onTab={setTab}
        isMain={main}
        counts={{ products: scoped.length, suppliers: suppliers.length, categories: categories.length }}
        onNewProduct={() => setProductDialog({ open: true, product: null })}
        onNewSupplier={() => setSupplierDialog({ open: true, supplier: null })}
        onNewCategory={() => setCategoryDialog({ open: true, category: null })}
      />

      {tab === "products" && (
        <ProductsTab
          search={search}
          onSearch={setSearch}
          cat={cat}
          onCat={setCat}
          categoryChips={categoryChips}
          filtered={filtered}
          supplierById={supplierById}
          isMain={main}
          canEditProduct={canEditProduct}
          onEdit={(p) => setProductDialog({ open: true, product: p })}
          onAskDelete={(p) => setConfirm({ kind: "product", id: p.id, name: p.name })}
        />
      )}

      {tab === "suppliers" && (
        <div className="bg-white border border-[#E6E0D4] rounded-xl divide-y divide-[#EEE8DC] overflow-hidden">
          {suppliers.map((s) => (
            <CatalogSupplierRow
              key={s.id}
              supplier={s}
              productCount={productCountBySupplier[s.id] || 0}
              isMain={main}
              onEdit={() => setSupplierDialog({ open: true, supplier: s })}
              onAskDelete={() => setConfirm({ kind: "supplier", id: s.id, name: s.name })}
            />
          ))}
        </div>
      )}

      {tab === "categories" && (
        <CategoriesTab
          categories={categories}
          onCategoriesChange={onCategoriesChange}
          productCountByCategory={productCountByCategory}
          isMain={main}
          onEdit={(c) => setCategoryDialog({ open: true, category: c })}
          onAskDelete={({ id, name, count }) =>
            setConfirm({ kind: "category", id, name, count })
          }
        />
      )}

      <ProductDialog
        open={productDialog.open}
        onOpenChange={(open) => setProductDialog((d) => ({ ...d, open }))}
        product={productDialog.product}
        suppliers={suppliers}
        categories={categories}
        user={user}
        onSave={saveProduct}
      />
      <SupplierDialog
        open={supplierDialog.open}
        onOpenChange={(open) => setSupplierDialog((d) => ({ ...d, open }))}
        supplier={supplierDialog.supplier}
        onSave={saveSupplier}
      />
      <CategoryDialog
        open={categoryDialog.open}
        onOpenChange={(open) => setCategoryDialog((d) => ({ ...d, open }))}
        category={categoryDialog.category}
        existing={categories}
        onSave={saveCategory}
      />

      <CatalogConfirmDialog
        confirm={confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
    </section>
  );
}
